import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { usePostHog } from "@posthog/react";

import protectedAxios from "../common/axios";
import { API_URL } from "../common/constants";
import { useInterval } from "../common/useInterval";
import { getUserEntitlements } from "../common/apis/marketplace.apis";
import {
  activityBackendSync,
  pushData,
} from "../common/activity/activityBackendSync";

const HEARTBEAT_INTERVAL = 1 * 60 * 1000;

const UserPresence = ({ children }) => {
  const posthog = usePostHog();
  const gamePlayerId = useSelector((state) => state.user?.profile?._id);
  const createdAtTimestamp = useSelector(
    (state) => state.user?.profile?.createdAt
  );
  const firstName = useSelector((state) => state.user?.profile?.firstName);
  const userName = useSelector((state) => state.user?.profile?.userName);
  const lastName = useSelector((state) => state.user?.profile?.lastName);
  const email = useSelector((state) => state.user?.profile?.email);
  const phone = useSelector((state) => state.user?.profile?.phone);
  const accessToken = useSelector((state) => state.user?.accessToken);

  const heartbeat = useCallback(() => {
    // execute user presence API only if user is logged in
    if (!gamePlayerId || !accessToken) return;

    var userId = gamePlayerId || null;
    if (userId) {
      try {
        if (window.clarity) window?.clarity("identify", userId);
      } catch (error) {
        console.error(error);
      }
    }

    try {
      if (window.clarity) {
        window?.clarity("set", "First Name", firstName);
        window?.clarity("set", "Last Name", lastName);
        window?.clarity("set", "Username", userName);
        window?.clarity("set", "Signed up At", createdAtTimestamp);
      }
    } catch (error) {
      console.error(error);
    }

    window?.dataLayer?.push({
      user_id: userId,
    });

    Sentry?.setUser({ id: gamePlayerId, username: userName, email: email });

    protectedAxios
      .post(`${API_URL}/v1/presence`, {
        entity_id: gamePlayerId,
      })
      .catch((error) => {
        console.log("Sorry, we are unable to sync with backend");
      });

    // Push recent activities of the users to
    // nbyula server
    pushData();
  }, [gamePlayerId, accessToken, pushData]);

  useEffect(() => {
    heartbeat();
    activityBackendSync();
  }, [heartbeat, email, firstName, lastName]);

  // Identify the logged-in user to PostHog so events attach to a real Person
  // (with name/email/phone) instead of an anonymous distinct_id. This lives in
  // the presence layer — rather than only in the OTP login flow — so returning
  // users restored from a persisted session (who never re-trigger OTP login)
  // are still identified on every visit. posthog-js de-dupes unchanged person
  // properties, so re-running this on profile changes is cheap.
  useEffect(() => {
    if (!posthog || !gamePlayerId || !accessToken) return;

    posthog.identify(gamePlayerId, {
      email: email || undefined,
      name:
        `${firstName || ""} ${lastName || ""}`.trim() || userName || undefined,
      phone: phone || undefined,
      username: userName || undefined,
      signed_up_at: createdAtTimestamp || undefined,
    });
  }, [
    posthog,
    gamePlayerId,
    accessToken,
    email,
    phone,
    firstName,
    lastName,
    userName,
    createdAtTimestamp,
  ]);

  // Attach the user's active plan and purchased services to their PostHog
  // Person so cohorts/funnels can be segmented by what they've bought. Fetched
  // once per logged-in user (keyed on id) rather than on the 60s heartbeat to
  // avoid hammering the entitlements endpoint. Failures are swallowed — this is
  // best-effort analytics enrichment and must never block the app.
  useEffect(() => {
    if (!posthog || !gamePlayerId || !accessToken) return;

    let cancelled = false;
    (async () => {
      try {
        const entitlements = await getUserEntitlements(gamePlayerId);
        if (cancelled || !entitlements) return;

        const activePlan = entitlements.activePlan || null;
        const services = Array.isArray(entitlements.services)
          ? entitlements.services
          : [];

        posthog.setPersonProperties({
          has_active_plan: Boolean(activePlan?.planId),
          active_plan_id: activePlan?.planId || null,
          active_plan_name:
            activePlan?.planName || activePlan?.name || null,
          purchased_services: services
            .map((service) => service.serviceName)
            .filter(Boolean),
          purchased_service_slugs: services
            .map((service) => service.serviceSlug)
            .filter(Boolean),
          purchased_services_count: services.length,
        });
      } catch (error) {
        console.error("Unable to sync entitlements to PostHog:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [posthog, gamePlayerId, accessToken]);

  useInterval(() => {
    heartbeat();
  }, HEARTBEAT_INTERVAL);

  return <>{children}</>;
};

export default UserPresence;
